import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// import { useAuth } from '../../contexts/AuthContext'; // Removed unused import
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  CreditCard, 
  Upload, 
  CheckCircle, 
  Clock,
  ArrowLeft,
  DollarSign,
  FileText,
  Copy,
  Landmark,
  Smartphone
} from 'lucide-react';

const PaymentPage = () => {
  // const { user } = useAuth(); // Removed unused variable
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [studentData, setStudentData] = useState(null);
  const [feeStructure, setFeeStructure] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState(null);

  useEffect(() => {
    fetchPaymentData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPaymentData = async () => {
    try {
      const [studentRes, feeRes, bankRes, historyRes] = await Promise.all([
        axios.get('/api/students/profile').catch(err => ({ data: null, status: err.response?.status })),
        axios.get('/api/payments/fee-structure'),
        axios.get('/api/payments/bank-details'),
        axios.get('/api/payments/history').catch(err => ({ data: null }))
      ]);
      
      setStudentData(studentRes.data);
      setFeeStructure(feeRes.data);
      setBankDetails(bankRes.data);
      setPaymentHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching payment data:', error);
      if (error.response?.status === 404) {
        toast.error('Please complete your profile first');
        navigate('/student/profile');
      } else {
        toast.error('Failed to load payment data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a valid image (JPG, PNG) or PDF file');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUploadReceipt = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('receipt', selectedFile);

      await axios.post('/api/students/upload-receipt', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Payment receipt uploaded successfully!');
      setSelectedFile(null);
      await fetchPaymentData();
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to upload receipt';
      toast.error(errorMessage);
      
      // Log current student status for debugging
      if (studentData) {
        console.log('Current student status:', studentData.status);
        console.log('Current payment status:', studentData.paymentStatus);
      }
      
      // If it's a status-related error, show more helpful message
      if (errorMessage.includes('status') || errorMessage.includes('allocated')) {
        toast.error(`Current status: ${studentData?.status || 'unknown'}. Please ensure your application is in the correct status before uploading receipt.`);
      }
    } finally {
      setUploading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard!`);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Payment Pending' },
      paid: { color: 'bg-blue-100 text-blue-800', icon: Upload, text: 'Receipt Uploaded' },
      verified: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Payment Verified' },
      rejected: { color: 'bg-red-100 text-red-800', icon: Clock, text: 'Payment Rejected' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="h-4 w-4 mr-2" />
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!studentData) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <Clock className="mx-auto h-12 w-12 text-yellow-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Profile Not Found
          </h2>
          <p className="text-gray-600 mb-4">
            Please complete your profile first before accessing the payment portal.
          </p>
          <button
            onClick={() => navigate('/student/profile')}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Complete Profile
          </button>
        </div>
      </div>
    );
  }

  const branchFee = feeStructure?.[studentData.allocatedBranch];

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/student')}
          className="flex items-center text-primary-600 hover:text-primary-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Payment Portal</h1>
        <p className="text-gray-600 mt-2">
          Complete your fee payment to secure your admission
        </p>
      </div>

             {/* Payment Status */}
       <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
         <div className="flex items-center justify-between">
           <div>
             <h2 className="text-xl font-semibold text-gray-900">Payment Status</h2>
             <p className="text-gray-600 mt-1">Current status of your fee payment</p>
           </div>
           {getStatusBadge(paymentHistory?.paymentStatus || 'pending')}
         </div>
         
         {/* Debug Info - Remove this in production */}
         <div className="mt-4 p-3 bg-gray-50 rounded text-xs">
           <div className="grid grid-cols-2 gap-2">
             <div><strong>Application Status:</strong> {studentData?.status || 'N/A'}</div>
             <div><strong>Payment Status:</strong> {studentData?.paymentStatus || 'N/A'}</div>
             <div><strong>Allocated Branch:</strong> {studentData?.allocatedBranch || 'Not allocated'}</div>
             <div><strong>Rank:</strong> {studentData?.rank || 'Not ranked'}</div>
           </div>
         </div>
       </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fee Structure */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            <DollarSign className="h-6 w-6 text-primary-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Fee Structure</h2>
          </div>
          
          <div className="space-y-4">
            {studentData.allocatedBranch ? (
              <>
                <div className="bg-primary-50 p-4 rounded-lg">
                  <h3 className="font-medium text-primary-900 mb-2">
                    {studentData.allocatedBranch}
                  </h3>
                  <p className="text-sm text-primary-700">Academic Year 2024-25</p>
                </div>

                {branchFee && (
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Tuition Fee</span>
                      <span className="font-medium">₹{branchFee.tuitionFee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Hostel Fee</span>
                      <span className="font-medium">₹{branchFee.hostelFee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Mess Fee</span>
                      <span className="font-medium">₹{branchFee.messFee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Other Charges</span>
                      <span className="font-medium">₹{branchFee.otherCharges.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-3 text-lg font-bold text-primary-600 bg-primary-50 px-4 rounded">
                      <span>Total Amount</span>
                      <span>₹{branchFee.total.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-yellow-400 mr-2" />
                  <div>
                    <h3 className="font-medium text-yellow-900">Branch Not Allocated Yet</h3>
                    <p className="text-sm text-yellow-700 mt-1">
                      Fee structure will be displayed once your branch is allocated. You can still upload payment receipts.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-6">
            <Landmark className="h-6 w-6 text-primary-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Payment Methods</h2>
          </div>

          <div className="space-y-4">
            {/* Bank Transfer */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Landmark className="h-5 w-5 text-gray-400 mr-2" />
                <h3 className="font-medium text-gray-900">Bank Transfer</h3>
              </div>
              
              {bankDetails && (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Account Name:</span>
                    <div className="flex items-center">
                      <span className="font-medium mr-2">{bankDetails.accountName}</span>
                      <button
                        onClick={() => copyToClipboard(bankDetails.accountName, 'Account name')}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Account Number:</span>
                    <div className="flex items-center">
                      <span className="font-medium mr-2">{bankDetails.accountNumber}</span>
                      <button
                        onClick={() => copyToClipboard(bankDetails.accountNumber, 'Account number')}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">IFSC Code:</span>
                    <div className="flex items-center">
                      <span className="font-medium mr-2">{bankDetails.ifscCode}</span>
                      <button
                        onClick={() => copyToClipboard(bankDetails.ifscCode, 'IFSC code')}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Bank Name:</span>
                    <span className="font-medium">{bankDetails.bankName}</span>
                  </div>
                </div>
              )}
            </div>

            {/* UPI Payment */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Smartphone className="h-5 w-5 text-gray-400 mr-2" />
                <h3 className="font-medium text-gray-900">UPI Payment</h3>
              </div>
              
              {bankDetails && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">UPI ID:</span>
                  <div className="flex items-center">
                    <span className="font-medium mr-2">{bankDetails.upiId}</span>
                    <button
                      onClick={() => copyToClipboard(bankDetails.upiId, 'UPI ID')}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      <Copy className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Upload Receipt Section */}
      {paymentHistory?.paymentStatus !== 'verified' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
          <div className="flex items-center mb-6">
            <Upload className="h-6 w-6 text-primary-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Upload Payment Receipt</h2>
          </div>

          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">Important Instructions:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Upload clear image of your payment receipt or transaction screenshot</li>
                <li>• Accepted formats: JPG, PNG, PDF (Max 5MB)</li>
                <li>• Ensure all transaction details are visible</li>
                <li>• Receipt will be verified by admin within 24-48 hours</li>
              </ul>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                id="receipt"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
              <label
                htmlFor="receipt"
                className="cursor-pointer flex flex-col items-center"
              >
                <FileText className="h-12 w-12 text-gray-400 mb-4" />
                <span className="text-sm font-medium text-gray-600">
                  {selectedFile ? selectedFile.name : 'Click to select receipt file'}
                </span>
                <span className="text-xs text-gray-500 mt-1">
                  JPG, PNG, PDF up to 5MB
                </span>
              </label>
            </div>

            {selectedFile && (
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm font-medium">{selectedFile.name}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                  <button
                    onClick={handleUploadReceipt}
                    disabled={uploading}
                    className="bg-primary-600 text-white px-4 py-2 rounded text-sm hover:bg-primary-700 disabled:opacity-50 transition-colors"
                  >
                    {uploading ? 'Uploading...' : 'Upload Receipt'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment History */}
      {paymentHistory?.paymentReceipt && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <CreditCard className="h-6 w-6 text-primary-600 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900">Payment History</h2>
            </div>
            {getStatusBadge(paymentHistory.paymentStatus)}
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Receipt Uploaded:</span>
                <span className="font-medium ml-2">
                  {new Date(paymentHistory.paymentReceipt.uploadedAt).toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-gray-600">File Name:</span>
                <span className="font-medium ml-2">{paymentHistory.paymentReceipt.filename}</span>
              </div>
            </div>
            
            {paymentHistory.paymentStatus === 'verified' && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-green-800 font-medium">
                    Payment verified successfully! Your offer letter has been generated.
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;